import {
  FetchInsurancesResponse,
  initialInsurancePolicy,
  InsurancePolicy,
  InsurancePolicyKey,
} from '@api/models'
import { toast, useModal } from '@components/common'
import {
  useCreateInsuranceMutation,
  useDeleteInsuranceMutation,
  useEditInsuranceMutation,
  useFetchInsurancesQuery,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation,
} from '@api'
import {
  InsuranceFormSubmissionData,
  StagedAttachment,
} from '@api/attachments/models/Attachment.model'
import { Dispatch, SetStateAction, useState } from 'react'
import { useTableParams } from '@hooks'
import { TableParamsHook } from 'hooks/useTableParams'
import { useAppDispatch } from '../../index'
import { attachmentsEndpoints } from '@api/attachments/attachmentsEndpoints'

export interface InsurancesHook extends TableParamsHook {
  filterState: Object
  setFilterState: Dispatch<SetStateAction<{}>>
  searchKey: string
  selectedInsurance: InsurancePolicy

  insurances: FetchInsurancesResponse
  isLoadingInsurances: boolean
  isErrorFetchingInsurances: boolean
  isRefetchingInsurances: boolean

  handleEditInsurance: (data: InsuranceFormSubmissionData) => Promise<void>
  handleCreateInsurance: (data: InsuranceFormSubmissionData) => Promise<void>
  isLoadingEditInsurance: boolean
  isLoadingCreateInsurance: boolean

  handleDeleteInsurance: (payload: InsurancePolicy) => Promise<void>
  isLoadingDeleteInsurance: boolean

  deleteInsuranceModalIsOpen: boolean
  triggerDeleteInsuranceModal
  closeDeleteInsuranceModal
  openDeleteInsuranceModal: (insurance: InsurancePolicy) => void
  openEditInsuranceModal: (insurance: InsurancePolicy) => void
  triggerEditInsuranceModal
  closeEditInsuranceModal
  editInsuranceModalIsOpen: boolean

  openCreateInsuranceModal: () => void
  createInsuranceModalIsOpen: boolean
  closeCreateInsuranceModal
}

export default function useInsurances(): InsurancesHook {
  const [filterState, setFilterState] = useState({})
  const [selectedInsurance, setSelectedInsurance] = useState<InsurancePolicy>(undefined)
  const dispatch = useAppDispatch()

  const searchKey: InsurancePolicyKey = 'IN_DESC'
  const initialItemsPerPage = 20

  // Use useTableParams hook for table filtering, pagination, etc.
  const {
    pageNumber,
    itemsPerPage,
    fetchRequestPayload,
    handleSearch,
    searchValue,
    changePage,
    sortByInitialState,
    updateSortSearchParam,
  } = useTableParams({
    searchKey,
    filterState,
    setFilterState,
    initialItemsPerPage,
  })

  // Fetch Insurances
  const {
    data: insurances,
    isLoading: isLoadingInsurances,
    isError: isErrorFetchingInsurances,
    isFetching: isRefetchingInsurances,
  } = useFetchInsurancesQuery(fetchRequestPayload)

  // MODAL STATES
  // Delete modal
  const {
    modalIsOpen: deleteInsuranceModalIsOpen,
    triggerModal: triggerDeleteInsuranceModal,
    closeModal: closeDeleteInsuranceModal,
  } = useModal()
  function openDeleteInsuranceModal(insurance: InsurancePolicy): void {
    setSelectedInsurance(insurance)
    triggerDeleteInsuranceModal()
  }

  // Edit modal
  const {
    modalIsOpen: editInsuranceModalIsOpen,
    triggerModal: triggerEditInsuranceModal,
    closeModal: closeEditInsuranceModal,
  } = useModal()

  function openEditInsuranceModal(insurance: InsurancePolicy): void {
    setSelectedInsurance(insurance)
    triggerEditInsuranceModal()
  }

  // Create modal
  const {
    modalIsOpen: createInsuranceModalIsOpen,
    triggerModal: triggerCreateInsuranceModal,
    closeModal: closeCreateInsuranceModal,
  } = useModal()
  function openCreateInsuranceModal(): void {
    setSelectedInsurance(initialInsurancePolicy)
    triggerCreateInsuranceModal()
  }

  // MUTATIONS
  // Delete
  const [
    deleteInsurance,
    { isLoading: isLoadingDeleteInsurance },
  ] = useDeleteInsuranceMutation()
  const [deleteAttachment] = useDeleteAttachmentMutation()

  async function handleDeleteInsurance(payload: InsurancePolicy): Promise<void> {
    try {
      const getInsuranceAttachmentsResult = await dispatch(
        attachmentsEndpoints.endpoints.getInsuranceAttachments.initiate({
          insuranceID: payload.IN_POL_ID,
        })
      )

      const attachments = getInsuranceAttachmentsResult.data || []

      await deleteInsurance(payload).unwrap()
      closeDeleteInsuranceModal()

      if (attachments.length > 0) {
        const attachmentDeletionPromises = attachments.map(attachment =>
          deleteAttachment({
            id: attachment.ID,
            type: 'Insurance',
          }).unwrap()
        )

        try {
          await Promise.all(attachmentDeletionPromises)
          toast.success(
            `Successfully deleted "${payload.IN_NAME}" and ${attachments.length} associated attachment(s) from your insurance policies.`
          )
        } catch (attachmentError) {
          console.warn('Some attachments failed to delete:', attachmentError)
          toast.success(
            `Successfully deleted "${payload.IN_NAME}" from your insurance policies, but ${attachments.length} attachment(s) could not be removed.`
          )
        }
      } else {
        toast.success(
          `Successfully deleted "${payload.IN_NAME}" from your insurance policies.`
        )
      }
    } catch (error: any) {
      toast.error(error.data || 'There was an unexpected error.')
    }
  }

  // Edit
  const [editInsurance, { isLoading: isLoadingEditInsurance }] = useEditInsuranceMutation()
  async function handleEditInsurance(data: InsuranceFormSubmissionData): Promise<void> {
    try {
      // Step 1: Update the insurance policy
      await editInsurance(data.insuranceData).unwrap()

      // Step 2: Upload staged attachments if any (for editing, we still allow adding new attachments)
      if (data.stagedAttachments.length > 0) {
        const uploadResults = await uploadStagedAttachments(
          data.insuranceData.IN_POL_ID,
          data.stagedAttachments
        )

        if (uploadResults.failed.length > 0) {
          toast.error(
            `Insurance policy updated successfully, but ${uploadResults.failed.length} attachment(s) failed to upload.`
          )
        } else if (uploadResults.successful > 0) {
          toast.success(
            `Insurance policy "${data.insuranceData.IN_NAME}" successfully updated with ${uploadResults.successful} new attachment(s).`
          )
        } else {
          toast.success(
            `Insurance policy "${data.insuranceData.IN_NAME}" successfully updated.`
          )
        }
      } else {
        toast.success(
          `Insurance policy "${data.insuranceData.IN_NAME}" successfully updated.`
        )
      }

      closeEditInsuranceModal()
    } catch (error: any) {
      toast.error(
        error.data || 'There was an unexpected error updating this insurance policy.'
      )
    }
  }

  // Create
  const [
    createInsurance,
    { isLoading: isLoadingCreateInsurance },
  ] = useCreateInsuranceMutation()
  const [uploadAttachment] = useUploadAttachmentMutation()

  async function uploadStagedAttachments(
    insuranceID: string,
    stagedAttachments: StagedAttachment[]
  ): Promise<{ successful: number; failed: StagedAttachment[] }> {
    const results = { successful: 0, failed: [] as StagedAttachment[] }

    const attachmentsToUpload = stagedAttachments.slice(0, 1)

    if (stagedAttachments.length > 1) {
      console.warn(
        'Only one attachment per insurance policy is allowed. Only the first attachment will be uploaded.'
      )
    }

    for (const staged of attachmentsToUpload) {
      try {
        const formData = new FormData()
        formData.append('file', staged.file)
        formData.append('assetCompanyID', '0') // Not needed for insurance
        formData.append('objectIDs', JSON.stringify([insuranceID]))
        formData.append('type', 'Insurance')
        formData.append('comment', staged.comment)

        await uploadAttachment(formData).unwrap()
        results.successful++
      } catch (error) {
        results.failed.push(staged)
      }
    }

    if (stagedAttachments.length > 1) {
      results.failed.push(...stagedAttachments.slice(1))
    }

    return results
  }

  async function handleCreateInsurance(data: InsuranceFormSubmissionData): Promise<void> {
    try {
      // Step 1: Create the insurance policy
      await createInsurance(data.insuranceData).unwrap()

      // Step 2: Upload staged attachments if any
      if (data.stagedAttachments.length > 0) {
        const uploadResults = await uploadStagedAttachments(
          data.insuranceData.IN_POL_ID,
          data.stagedAttachments
        )

        if (uploadResults.failed.length > 0) {
          toast.error(
            `Insurance policy created successfully, but ${uploadResults.failed.length} attachment(s) failed to upload.`
          )
        } else if (uploadResults.successful > 0) {
          toast.success(
            `Successfully added ${data.insuranceData.IN_NAME} to insurance policies with ${uploadResults.successful} attachment(s).`
          )
        } else {
          toast.success(
            `Successfully added ${data.insuranceData.IN_NAME} to insurance policies.`
          )
        }
      } else {
        toast.success(
          `Successfully added ${data.insuranceData.IN_NAME} to insurance policies.`
        )
      }

      closeCreateInsuranceModal()
    } catch (error: any) {
      toast.error(
        error.data || 'There was an unexpected error creating the insurance policy.'
      )
    }
  }

  return {
    // tableParams return
    pageNumber,
    itemsPerPage,
    fetchRequestPayload,
    handleSearch,
    searchValue,
    changePage,
    sortByInitialState,
    updateSortSearchParam,
    //
    filterState,
    setFilterState,
    searchKey,
    selectedInsurance,

    insurances,
    isLoadingInsurances,
    isErrorFetchingInsurances,
    isRefetchingInsurances,

    handleDeleteInsurance,
    isLoadingDeleteInsurance,

    handleEditInsurance,
    handleCreateInsurance,
    isLoadingCreateInsurance,
    isLoadingEditInsurance,

    deleteInsuranceModalIsOpen,
    triggerDeleteInsuranceModal,
    closeDeleteInsuranceModal,
    openDeleteInsuranceModal,
    openEditInsuranceModal,
    triggerEditInsuranceModal,
    closeEditInsuranceModal,
    editInsuranceModalIsOpen,

    createInsuranceModalIsOpen,
    openCreateInsuranceModal,
    closeCreateInsuranceModal,
  }
}
